import { DEFAULT_TONE_COLOR_SETTINGS } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { bool, crudTable, integer, text } from "@-/dev.improve.haniverse.lib.database.table";
import {
  EMPTY_CUSTOM_LEVEL_PACKS,
  type CustomLevelPacks,
} from "@-/dev.improve.haniverse.lib.level.custom";
import {
  DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS,
  type PrimaryNavigationDestination,
} from "#primary-navigation-destinations-setting";
import {
  type CorpusSettings,
  DEFAULT_CORPUS_SETTINGS,
} from "@-/dev.improve.haniverse.lib.morpheme.corpus.settings";

export const DEFAULT_USER_SETTINGS = {
  corpusSettings: DEFAULT_CORPUS_SETTINGS,
  customLevelPacks: EMPTY_CUSTOM_LEVEL_PACKS,
  dailyNewGraphemeLimit: null,
  hanziScript: "simplified",
  hskVersion: "hsk2026",
  isMicDrillsEnabled: true,
  isPinyinFlashcardsEnabled: false,
  isPinyinMnemonicsEnabled: false,
  isQuizCardsInStatsEnabled: false,
  isSentenceCharacterDrillEnabled: false,
  isSentenceSpokenDrillEnabled: false,
  isToneColorsEnabled: true,
  isWordSentenceCardsEnabled: false,
  pinyinKeyboardLayout: "xiaohe",
  primaryNavigationDestinations: DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS,
  reviewMode: "answer",
  weekStartsOn: "monday",
  ...DEFAULT_TONE_COLOR_SETTINGS,
} as const;

// Singleton row anchor for the actor (synced) user. Carries the CRUD common
// columns (`id`, timestamps, soft-delete) that `crudTable` injects plus every
// per-user setting that syncs across the user's devices. (The former standalone
// `user_settings` table was folded in here — one singleton row, not two.)
//
// Its id is always `ACTOR_USER_ID`, so every device creates the same row. The id says nothing
// about whose data this is: the account that owns a database is recorded outside it.
export const userTable = crudTable("user", {
  customLevelPacks: text("custom_level_packs", { mode: "json" })
    .$type<CustomLevelPacks>()
    .notNull()
    .default(DEFAULT_USER_SETTINGS.customLevelPacks),
  primaryNavigationDestinations: text("primary_navigation_destinations", { mode: "json" })
    .$type<readonly PrimaryNavigationDestination[]>()
    .notNull()
    .default(DEFAULT_USER_SETTINGS.primaryNavigationDestinations),
  /**
   * Which characters and words the NON-curated surfaces (search, browse tabs, compounds, similar
   * characters, words-containing lists, …) show, per script. JSON validated by
   * `corpusSettingsSchema`; read it through `resolveCorpusSettings` so a malformed or
   * newer-version value falls back to the defaults. See `corpus-settings.ts`.
   */
  corpusSettings: text("corpus_settings", { mode: "json" })
    .$type<CorpusSettings>()
    .notNull()
    .default(DEFAULT_USER_SETTINGS.corpusSettings),
  hanziScript: text("hanzi_script", { enum: ["traditional", "simplified"] })
    .notNull()
    .default(DEFAULT_USER_SETTINGS.hanziScript),

  hskVersion: text("hsk_version", { enum: ["hsk2", "hsk3", "hsk2026"] })
    .notNull()
    .default(DEFAULT_USER_SETTINGS.hskVersion),
  /**
   * Master switch for every drill that requires the microphone.
   *
   * Defaults ON because it does not decide whether
   * a drill is WANTED — it records that the user CAN'T use one right now. The speaking drill offers
   * a "Can't speak? Skip" escape the first time it appears in a session, which flips this off so a
   * user in a library or on a bus is not stuck on a card they cannot answer.
   *
   * Deliberately separate from `isSentenceSpokenDrillEnabled`: that one is a preference ("I want to
   * practise speaking"), this one is a circumstance ("not right now"). Collapsing them would mean
   * skipping once silently opts the user out of a drill they chose, with no way to tell the two
   * apart later.
   */
  isMicDrillsEnabled: bool("is_mic_drills_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isMicDrillsEnabled),
  isPinyinFlashcardsEnabled: bool("is_pinyin_flashcards_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isPinyinFlashcardsEnabled),
  isPinyinMnemonicsEnabled: bool("is_pinyin_mnemonics_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isPinyinMnemonicsEnabled),
  // Quiz cards (authored `:::quiz` checks) are reviewed like any card, but learners read stats as
  // being about their characters, so forecast and inventory stats (Upcoming, stage counts, deck
  // lists) leave them out unless this is on. Due counts and review activity always include them.
  isQuizCardsInStatsEnabled: bool("is_quiz_cards_in_stats_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isQuizCardsInStatsEnabled),
  // Gates the two sentence-production drills — produce a whole Chinese sentence from its English
  // translation, by typing (`character`) or by speaking (`spoken`). Same convention as the two
  // gates above: the cards are authored regardless, this only controls review eligibility.
  isSentenceCharacterDrillEnabled: bool("is_sentence_character_drill_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isSentenceCharacterDrillEnabled),
  isSentenceSpokenDrillEnabled: bool("is_sentence_spoken_drill_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isSentenceSpokenDrillEnabled),
  isToneColorsEnabled: bool("is_tone_colors_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isToneColorsEnabled),
  // Gates whether word cards (the sentence-context drill) enter the review rotation. Word cards
  // are authored regardless; this only controls eligibility, mirroring how
  // `isPinyinFlashcardsEnabled` gates pronunciation cards.
  isWordSentenceCardsEnabled: bool("is_word_sentence_cards_enabled")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.isWordSentenceCardsEnabled),
  pinyinKeyboardLayout: text("pinyin_keyboard_layout", {
    enum: ["qwerty", "ziranma", "sogou", "pinyin-jiajia", "weiruan", "xiaohe", "changyong"],
  })
    .notNull()
    .default(DEFAULT_USER_SETTINGS.pinyinKeyboardLayout),
  reviewMode: text("review_mode", { enum: ["manual", "answer"] })
    .notNull()
    .default(DEFAULT_USER_SETTINGS.reviewMode),

  tone0ColorDarkHex: text("tone0_color_dark_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone0ColorDarkHex),
  tone0ColorDarkName: text("tone0_color_dark_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone0ColorDarkName),
  tone0ColorLightHex: text("tone0_color_light_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone0ColorLightHex),
  tone0ColorLightName: text("tone0_color_light_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone0ColorLightName),

  tone1ColorDarkHex: text("tone1_color_dark_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone1ColorDarkHex),
  tone1ColorDarkName: text("tone1_color_dark_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone1ColorDarkName),
  tone1ColorLightHex: text("tone1_color_light_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone1ColorLightHex),
  tone1ColorLightName: text("tone1_color_light_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone1ColorLightName),

  tone2ColorDarkHex: text("tone2_color_dark_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone2ColorDarkHex),
  tone2ColorDarkName: text("tone2_color_dark_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone2ColorDarkName),
  tone2ColorLightHex: text("tone2_color_light_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone2ColorLightHex),
  tone2ColorLightName: text("tone2_color_light_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone2ColorLightName),

  tone3ColorDarkHex: text("tone3_color_dark_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone3ColorDarkHex),
  tone3ColorDarkName: text("tone3_color_dark_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone3ColorDarkName),
  tone3ColorLightHex: text("tone3_color_light_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone3ColorLightHex),
  tone3ColorLightName: text("tone3_color_light_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone3ColorLightName),

  tone4ColorDarkHex: text("tone4_color_dark_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone4ColorDarkHex),
  tone4ColorDarkName: text("tone4_color_dark_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone4ColorDarkName),
  tone4ColorLightHex: text("tone4_color_light_hex")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone4ColorLightHex),
  tone4ColorLightName: text("tone4_color_light_name")
    .notNull()
    .default(DEFAULT_USER_SETTINGS.tone4ColorLightName),

  /**
   * Which weekday a calendar week starts on, for every week-bucketed metric (see
   * `studyPeriodStart` in `lib/study-time`): the Stats "This week" total and the
   * week pager both anchor to this. Defaults to `monday` — the ISO-8601 week and
   * the behaviour that shipped before this was configurable — so existing users
   * see no change until they pick otherwise.
   *
   * Account-synced rather than device-local: a week boundary is a property of how
   * the user reads their own history, so the same account must bucket its stats
   * identically on every device. Only affects true CALENDAR-week metrics; the
   * rolling "last 7 days" windows elsewhere in Stats have no week start to honor.
   */
  weekStartsOn: text("week_starts_on", { enum: ["sunday", "monday"] })
    .notNull()
    .default(DEFAULT_USER_SETTINGS.weekStartsOn),
  /**
   * How many NEW graphemes the learner wants introduced per local day (Settings → Daily New
   * Cards); `null` is "No Limit". The free tier still holds a free learner to its own cap, so read
   * the day's effective limit through `resolveDailyNewGraphemeLimit`
   * (`lib/learn/new-grapheme-limit`), never this column alone.
   *
   * Nullable with no default so the column is an additive migration, and an account that never
   * chose a limit keeps the behaviour that shipped before it was configurable.
   */
  dailyNewGraphemeLimit: integer("daily_new_grapheme_limit"),
});
export type User = typeof userTable.$inferSelect;
